import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Howl } from 'howler';
import soundtrack from './assets/soundtrack.mp3';
import './ballball.css';

const TILE_SIZE = 1.6;
const MAZE_LAYOUTS = [
  [
    '###################',
    '#o...............o#',
    '#.###.#.#.###.#.#.#',
    '#.....#...#...#...#',
    '#.#.#.#####.###.#.#',
    '#...#...#...#...#.#',
    '#.#.###.#.###.#.#.#',
    '...................',
    '#.###.#####.###.#.#',
    '#o......#......o#.#',
    '#.#####...#####.#.#',
    '#.......#.........#',
    '###################',
  ],
  [
    '###################',
    '#o.....#...#.....o#',
    '#.###.#.#.#.#.###.#',
    '#...#...#.#...#...#',
    '#.#.#.###.###.#.#.#',
    '#.#...#.....#...#.#',
    '#.#.###.#.#.###.#.#',
    '...................',
    '#.###.###.###.###.#',
    '#o....#.....#....o#',
    '#.###.#.###.#.###.#',
    '#.....#...#.#.....#',
    '###################',
  ],
  [
    '###################',
    '#o..#...#...#...o.#',
    '#.#.#.#.#.#.#.#.#.#',
    '#...#.....#.....#.#',
    '#.###.###.#.###.###',
    '#.#...#...#...#...#',
    '#.#.#.#.#.#.#.#.#.#',
    '...................',
    '#.###.#.#####.#.###',
    '#o....#...#...#..o#',
    '#.###.###.#.###.#.#',
    '#.....#.......#...#',
    '###################',
  ],
];
const PACMAN_START_CELL = { row: 11, col: 1 };
const GHOST_START_CELLS = [
  { row: 6, col: 9 },
  { row: 6, col: 7 },
  { row: 5, col: 11 },
  { row: 5, col: 9 },
];
const MAZE_ROWS = MAZE_LAYOUTS[0].length;
const MAZE_COLS = MAZE_LAYOUTS[0][0].length;
const MAZE_HALF_WIDTH = ((MAZE_COLS - 1) * TILE_SIZE) / 2;
const MAZE_HALF_HEIGHT = ((MAZE_ROWS - 1) * TILE_SIZE) / 2;
const TUNNEL_LEFT_X = -MAZE_HALF_WIDTH;
const TUNNEL_RIGHT_X = MAZE_HALF_WIDTH;
const INITIAL_LIVES = 3;

// Finds the horizontal tunnel row for a specific maze by checking open left/right edges.
const getTunnelRowForLayout = (layout) => layout.findIndex(
  row => row[0] !== '#' && row[MAZE_COLS - 1] !== '#'
);

// Ghost colors matching classic Pac-Man: Blinky (red), Pinky (pink), Inky (cyan), Clyde (orange)
const ghostColors = [0xff0000, 0xffb8ff, 0x00ffff, 0xffb852];

// Creates a Three.js ShapeGeometry representing a Pac-Man pie wedge for the given mouth angle.
const createPacmanShape = (mouthAngle) => {
  const shape = new THREE.Shape();
  const radius = 0.85;
  const startAngle = mouthAngle;
  const endAngle = Math.PI * 2 - mouthAngle;
  shape.moveTo(0, 0);
  shape.absarc(0, 0, radius, startAngle, endAngle, false);
  shape.lineTo(0, 0);
  return new THREE.ShapeGeometry(shape, 32);
};

// Creates a high-resolution ghost body silhouette with rounded head and wavy skirt.
const createGhostShape = () => {
  const shape = new THREE.Shape();
  const r = 0.72;
  shape.moveTo(-r, -r);

  // Wavy skirt: softer and denser for a less polygonal look.
  shape.bezierCurveTo(-r, -r * 1.35, -r * 0.72, -r * 1.36, -r * 0.56, -r);
  shape.bezierCurveTo(-r * 0.56, -r * 1.3, -r * 0.28, -r * 1.32, -r * 0.14, -r);
  shape.bezierCurveTo(-r * 0.14, -r * 1.28, r * 0.14, -r * 1.3, r * 0.24, -r);
  shape.bezierCurveTo(r * 0.24, -r * 1.27, r * 0.5, -r * 1.3, r * 0.62, -r);
  shape.bezierCurveTo(r * 0.62, -r * 1.34, r * 0.9, -r * 1.35, r, -r);

  shape.lineTo(r, 0);
  shape.absarc(0, 0, r, 0, Math.PI, true);
  shape.lineTo(-r, -r);
  return new THREE.ShapeGeometry(shape, 64);
};

// Builds a ghost with layered materials, glow and eye rig for a more realistic render.
const createGhostMesh = (color) => {
  const ghost = new THREE.Group();

  const bodyGeometry = createGhostShape();
  const bodyMaterial = new THREE.MeshPhongMaterial({
    color,
    emissive: new THREE.Color(color).multiplyScalar(0.28),
    specular: new THREE.Color(0xffffff),
    shininess: 70,
    transparent: true,
    opacity: 0.84,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.renderOrder = 2;
  ghost.add(body);

  // Soft inner pass to add fake volume.
  const innerGeometry = bodyGeometry.clone();
  const innerMaterial = new THREE.MeshBasicMaterial({
    color: new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.2),
    transparent: true,
    opacity: 0.16,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const inner = new THREE.Mesh(innerGeometry, innerMaterial);
  inner.position.z = 0.01;
  inner.scale.set(0.87, 0.87, 1);
  inner.renderOrder = 3;
  ghost.add(inner);

  // Outer aura.
  const glowGeometry = bodyGeometry.clone();
  const glowMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.2,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const glow = new THREE.Mesh(glowGeometry, glowMaterial);
  glow.position.z = -0.01;
  glow.scale.set(1.16, 1.16, 1);
  glow.renderOrder = 1;
  ghost.add(glow);

  // Wide halo pass to reinforce neon ghost glow.
  const haloGeometry = bodyGeometry.clone();
  const haloMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.12,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const halo = new THREE.Mesh(haloGeometry, haloMaterial);
  halo.position.z = -0.02;
  halo.scale.set(1.34, 1.34, 1);
  halo.renderOrder = 0;
  ghost.add(halo);

  const eyeGeo = new THREE.CircleGeometry(0.14, 24);
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const pupilGeo = new THREE.CircleGeometry(0.065, 18);
  const pupilMat = new THREE.MeshBasicMaterial({ color: 0x10257a });
  const sparkleGeo = new THREE.CircleGeometry(0.03, 10);
  const sparkleMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85 });

  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.25, 0.23, 0.05);
  leftEye.scale.set(0.95, 1.08, 1);
  ghost.add(leftEye);

  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.25, 0.23, 0.05);
  rightEye.scale.set(0.95, 1.08, 1);
  ghost.add(rightEye);

  const leftPupil = new THREE.Mesh(pupilGeo, pupilMat);
  leftPupil.position.set(-0.25, 0.23, 0.07);
  ghost.add(leftPupil);

  const rightPupil = new THREE.Mesh(pupilGeo, pupilMat);
  rightPupil.position.set(0.25, 0.23, 0.07);
  ghost.add(rightPupil);

  const leftSparkle = new THREE.Mesh(sparkleGeo, sparkleMat);
  leftSparkle.position.set(-0.29, 0.28, 0.08);
  ghost.add(leftSparkle);

  const rightSparkle = new THREE.Mesh(sparkleGeo, sparkleMat);
  rightSparkle.position.set(0.21, 0.28, 0.08);
  ghost.add(rightSparkle);

  ghost.userData.body = body;
  ghost.userData.glow = glow;
  ghost.userData.bodyMaterial = bodyMaterial;
  ghost.userData.innerMaterial = innerMaterial;
  ghost.userData.glowMaterial = glowMaterial;
  ghost.userData.haloMaterial = haloMaterial;
  ghost.userData.glowMesh = glow;
  ghost.userData.haloMesh = halo;
  ghost.userData.pupils = [leftPupil, rightPupil];
  ghost.userData.basePupilPositions = [
    new THREE.Vector3(-0.25, 0.23, 0.07),
    new THREE.Vector3(0.25, 0.23, 0.07),
  ];

  return ghost;
};

// Renders the BallBall arcade game loop, menus, and soundtrack playback.
const BallBall = () => {
  const containerRef = useRef(null);
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const pacmanRef = useRef(null);
  const pacmanGlowRef = useRef(null);
  const pacmanMouthAngleRef = useRef(0.25);
  const pacmanMouthDirRef = useRef(1);
  const pacmanGroupRef = useRef(null);
  const ghostsRef = useRef([]);
  const wallsRef = useRef([]);
  const dotsRef = useRef([]);
  const powerUpsRef = useRef([]);
  const directionRef = useRef({ x: 0, y: 0 });
  const desiredDirectionRef = useRef({ x: 0, y: 0 });
  const speedRef = useRef(5.2);
  const ghostSpeedRef = useRef(3);
  const powerUpActiveRef = useRef(false);
  const powerUpTimerRef = useRef(null);
  const ghostParticlesRef = useRef([]);
  const animationFrameRef = useRef(null);
  const lifeRespawnTimeoutRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const pointLightRef = useRef(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [isInvincible, setIsInvincible] = useState(false);
  const powerUpActiveStateRef = useRef(false);
  const [powerUpTime, setPowerUpTime] = useState(0);
  const [dotsLeft, setDotsLeft] = useState(100);
  const [showMenu, setShowMenu] = useState(true);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [isMusicEnabled, setIsMusicEnabled] = useState(true);
  const [musicVolume, setMusicVolume] = useState(0.3);
  const [sfxVolume, setSfxVolume] = useState(0.5);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });
  const mazeLayoutRef = useRef(MAZE_LAYOUTS[0]);
  const mazeIndexRef = useRef(0);
  const tunnelRowRef = useRef(getTunnelRowForLayout(MAZE_LAYOUTS[0]));

  // Refs that mirror the boolean state variables so event handlers always
  // read the current (non-stale) values even when registered once at mount.
  const gameStartedRef = useRef(false);
  const gameOverRef = useRef(false);
  const livesRef = useRef(INITIAL_LIVES);
  const showMenuRef = useRef(true);
  const showHowToPlayRef = useRef(false);
  const showSettingsRef = useRef(false);
  const showCreditsRef = useRef(false);

  const isTouchOnInteractiveElement = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, input, select, textarea, label, a'));
  };

  // Applies a selected maze index and keeps refs in sync for collision and tunnel logic.
  const applyMazeLayoutByIndex = (layoutIndex) => {
    const safeIndex = Math.max(0, Math.min(MAZE_LAYOUTS.length - 1, layoutIndex));
    const nextLayout = MAZE_LAYOUTS[safeIndex];
    mazeLayoutRef.current = nextLayout;
    mazeIndexRef.current = safeIndex;
    tunnelRowRef.current = getTunnelRowForLayout(nextLayout);
  };

  // Chooses a random maze index different from the current one to vary each level transition.
  const getRandomNextMazeIndex = () => {
    if (MAZE_LAYOUTS.length <= 1) return 0;
    const randomOffset = Math.floor(Math.random() * (MAZE_LAYOUTS.length - 1)) + 1;
    return (mazeIndexRef.current + randomOffset) % MAZE_LAYOUTS.length;
  };

  // Picks and applies the next random maze using refs to avoid stale closure bugs.
  const applyRandomMazeLayout = () => {
    const nextIndex = getRandomNextMazeIndex();
    applyMazeLayoutByIndex(nextIndex);
  };

  // Sound refs initialized with Howler for music and stub objects for SFX
  const backgroundMusicRef = useRef(null);
  const audioContextRef = useRef(null);
  if (!backgroundMusicRef.current) {
    backgroundMusicRef.current = new Howl({ src: [soundtrack], loop: true, volume: musicVolume });
  }

  // Lazily creates and resumes the Web Audio context for synth effects.
  const ensureAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  };

  // Plays a tiny 8-bit style synth sound for game feedback based on event type.
  const playSfx = (type) => {
    const audioContext = ensureAudioContext();
    if (!audioContext) return;
    const now = audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.type = 'square';

    if (type === 'dot') {
      oscillator.frequency.setValueAtTime(880, now);
      gainNode.gain.setValueAtTime(sfxVolume * 0.4, now);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
      oscillator.start(now);
      oscillator.stop(now + 0.09);
    }

    if (type === 'powerup') {
      oscillator.frequency.setValueAtTime(440, now);
      oscillator.frequency.exponentialRampToValueAtTime(880, now + 0.18);
      gainNode.gain.setValueAtTime(sfxVolume * 0.6, now);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
      oscillator.start(now);
      oscillator.stop(now + 0.22);
    }

    if (type === 'ghost') {
      oscillator.frequency.setValueAtTime(600, now);
      oscillator.frequency.exponentialRampToValueAtTime(220, now + 0.22);
      gainNode.gain.setValueAtTime(sfxVolume * 0.7, now);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
      oscillator.start(now);
      oscillator.stop(now + 0.26);
    }

    if (type === 'gameover') {
      oscillator.frequency.setValueAtTime(392, now);
      oscillator.frequency.setValueAtTime(196, now + 0.25);
      oscillator.frequency.setValueAtTime(110, now + 0.45);
      gainNode.gain.setValueAtTime(sfxVolume * 0.8, now);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
      oscillator.start(now);
      oscillator.stop(now + 0.62);
    }

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
  };

  // Resizes the renderer and camera to match the mount container with safe fallbacks.
  // Also adjusts camera.position.z so the entire maze fits in view regardless of aspect ratio.
  const updateRendererSize = () => {
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    const mount = mountRef.current;
    if (!renderer || !camera || !mount) return;
    const bounds = mount.getBoundingClientRect();
    const width = bounds.width || mount.clientWidth || window.innerWidth || 1;
    const height = bounds.height || mount.clientHeight || window.innerHeight || 1;
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;

    // Compute camera z so that the full maze fits in both axes at the current aspect ratio.
    const mazeWorldWidth = (MAZE_COLS - 1) * TILE_SIZE;
    const mazeWorldHeight = (MAZE_ROWS - 1) * TILE_SIZE;
    const fovRad = (camera.fov * Math.PI) / 180;
    const aspect = width / height;
    // Distance needed to fit the maze vertically
    const zByHeight = (mazeWorldHeight / 2) / Math.tan(fovRad / 2) * 1.15;
    // Distance needed to fit the maze horizontally
    const zByWidth = (mazeWorldWidth / 2) / (Math.tan(fovRad / 2) * aspect) * 1.15;
    // Use the larger value so the maze fits along both axes simultaneously
    camera.position.z = Math.max(zByHeight, zByWidth);
    camera.updateProjectionMatrix();
  };

  // Queues a resize on the next animation frame to ensure layout is fully ready.
  const queueRendererResize = () => {
    requestAnimationFrame(() => {
      updateRendererSize();
    });
  };

  // Converts grid row/column to world-space XY centered in the maze.
  const gridToWorld = (row, col) => {
    return {
      x: col * TILE_SIZE - MAZE_HALF_WIDTH,
      y: MAZE_HALF_HEIGHT - row * TILE_SIZE,
    };
  };

  // Converts world-space XY to the nearest grid cell row/column.
  const worldToGrid = (position) => {
    const col = Math.round((position.x + MAZE_HALF_WIDTH) / TILE_SIZE);
    const row = Math.round((MAZE_HALF_HEIGHT - position.y) / TILE_SIZE);
    return {
      row: Math.max(0, Math.min(MAZE_ROWS - 1, row)),
      col: Math.max(0, Math.min(MAZE_COLS - 1, col)),
    };
  };

  // Applies classic Pac-Man horizontal tunnel teleport on the center row.
  const applyHorizontalTunnelWrap = (object3D) => {
    if (!object3D) return;
    const cell = worldToGrid(object3D.position);
    if (cell.row !== tunnelRowRef.current) return;

    if (object3D.position.x < -MAZE_HALF_WIDTH - TILE_SIZE * 0.1) {
      object3D.position.x = TUNNEL_RIGHT_X;
    } else if (object3D.position.x > MAZE_HALF_WIDTH + TILE_SIZE * 0.1) {
      object3D.position.x = TUNNEL_LEFT_X;
    }
  };

  // Returns true when a grid cell is within maze bounds and not a wall tile.
  const isWalkable = (row, col) => {
    if (row < 0 || row >= MAZE_ROWS || col < 0 || col >= MAZE_COLS) return false;
    return mazeLayoutRef.current[row][col] !== '#';
  };

  // Returns true when the given cell is a designated Pac-Man or ghost spawn position.
  const isStartCell = (row, col) => {
    if (row === PACMAN_START_CELL.row && col === PACMAN_START_CELL.col) return true;
    return GHOST_START_CELLS.some(cell => cell.row === row && cell.col === col);
  };

  // Creates a small white dot mesh (classic Pac-Man pellet) at the given grid cell.
  const createDotMesh = (row, col) => {
    const dotGeometry = new THREE.CircleGeometry(0.18, 12);
    const dotMaterial = new THREE.MeshBasicMaterial({ color: 0xffd700 });
    const dot = new THREE.Mesh(dotGeometry, dotMaterial);
    const position = gridToWorld(row, col);
    dot.position.set(position.x, position.y, 0.05);
    return dot;
  };

  // Creates a large glowing power-pellet mesh at the given grid cell,
  // pulsing its emissive intensity over time for a classic arcade look.
  const createPowerUpMesh = (row, col) => {
    const powerUpGeometry = new THREE.CircleGeometry(0.42, 24);
    const powerUpMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
    });
    const powerUp = new THREE.Mesh(powerUpGeometry, powerUpMaterial);
    const position = gridToWorld(row, col);
    powerUp.position.set(position.x, position.y, 0.05);

    // Pulsing glow ring around the power pellet
    const glowGeometry = new THREE.RingGeometry(0.42, 0.62, 24);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
    });
    const glowRing = new THREE.Mesh(glowGeometry, glowMaterial);
    glowRing.position.set(0, 0, 0.01);
    powerUp.add(glowRing);
    powerUp.userData.glowRing = glowRing;
    return powerUp;
  };

  // Returns the next grid cell when moving from a cell in a given direction vector.
  const getCellInDirection = (cell, direction) => {
    if (cell.row === tunnelRowRef.current) {
      if (direction.x === 1 && cell.col === MAZE_COLS - 1) {
        return { row: tunnelRowRef.current, col: 0 };
      }
      if (direction.x === -1 && cell.col === 0) {
        return { row: tunnelRowRef.current, col: MAZE_COLS - 1 };
      }
    }
    return {
      row: cell.row - direction.y,
      col: cell.col + direction.x,
    };
  };

  // Collects all walkable direction vectors from a grid cell, excluding walls.
  const getValidDirections = (row, col) => {
    const directions = [
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 },
    ];
    return directions.filter(direction => {
      const nextCell = getCellInDirection({ row, col }, direction);
      return isWalkable(nextCell.row, nextCell.col);
    });
  };

  // Ensures the render loop is running after component remounts.
  const ensureAnimationLoop = () => {
    if (animationFrameRef.current) return;
    animate();
  };

  // Builds the entire Three.js scene: camera, renderer, lighting, maze, Pac-Man, ghosts, dots.
  const initScene = () => {
    if (!mountRef.current) return;
    // Reattach canvas if React remounts without disposing the renderer
    if (rendererRef.current) {
      if (rendererRef.current.domElement && !mountRef.current.contains(rendererRef.current.domElement)) {
        mountRef.current.appendChild(rendererRef.current.domElement);
        queueRendererResize();
      }
      ensureAnimationLoop();
      return;
    }

    // Scene with pure black background matching classic Pac-Man arcade look
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.set(0, 0, 35);
    camera.lookAt(0, 0, 0);

    // Use antialias false for pixelated retro look
    const renderer = new THREE.WebGLRenderer({ antialias: false });
    renderer.setClearColor(0x000000, 1);
    mountRef.current.appendChild(renderer.domElement);
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    queueRendererResize();

    // Ambient light provides base illumination for the retro screen atmosphere
    const ambientLight = new THREE.AmbientLight(0x222244, 1.2);
    scene.add(ambientLight);

    // Point light at center gives a subtle "screen glow" effect over the maze
    const pointLight = new THREE.PointLight(0x0033ff, 1.2, 60);
    pointLight.position.set(0, 0, 8);
    scene.add(pointLight);
    pointLightRef.current = pointLight;

    // Secondary warm point light to simulate retro CRT warmth
    const warmLight = new THREE.PointLight(0x220022, 0.6, 40);
    warmLight.position.set(0, -8, 6);
    scene.add(warmLight);

    // Build maze walls and populate dots
    createMaze();
    createDotsAndPowerUps();

    // Pac-Man: a ShapeGeometry pie wedge, bright arcade yellow, rendered as a flat shape
    buildPacman(scene);

    // Ghosts: classic silhouette shapes in their traditional 4 colors
    spawnGhosts();

    animate();
  };

  // Builds the Pac-Man mesh as a pie-wedge ShapeGeometry inside a pivot group
  // so the mouth animation works correctly by rotating the geometry's arc.
  const buildPacman = (scene) => {
    const pacmanStart = gridToWorld(PACMAN_START_CELL.row, PACMAN_START_CELL.col);

    // Group used as the position/rotation pivot for Pac-Man
    const group = new THREE.Group();
    group.position.set(pacmanStart.x, pacmanStart.y, 0.1);
    scene.add(group);
    pacmanGroupRef.current = group;

    // Initial mouth open angle (radians from axis)
    const mouthAngle = 0.25;
    const pacmanGeometry = createPacmanShape(mouthAngle);
    const pacmanMaterial = new THREE.MeshBasicMaterial({
      color: 0xffe000,
      side: THREE.DoubleSide,
    });
    const pacman = new THREE.Mesh(pacmanGeometry, pacmanMaterial);
    group.add(pacman);
    pacmanRef.current = pacman;

    // Glow effect: a slightly larger transparent circle behind Pac-Man
    const glowGeo = new THREE.CircleGeometry(1.05, 24);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xffe000,
      transparent: true,
      opacity: 0,
    });
    const pacmanGlow = new THREE.Mesh(glowGeo, glowMat);
    pacmanGlow.position.set(0, 0, -0.02);
    group.add(pacmanGlow);
    pacmanGlowRef.current = pacmanGlow;
  };

  // Resets positions, power-ups, and dots for a new level or after restart.
  const resetLevelState = (useRandomMaze = false) => {
    if (useRandomMaze) {
      applyRandomMazeLayout();
      createMaze();
    }
    const group = pacmanGroupRef.current;
    if (group) {
      const startPosition = gridToWorld(PACMAN_START_CELL.row, PACMAN_START_CELL.col);
      group.position.set(startPosition.x, startPosition.y, 0.1);
      group.rotation.z = 0;
    }
    if (pacmanGlowRef.current) pacmanGlowRef.current.material.opacity = 0;
    directionRef.current = { x: 0, y: 0 };
    desiredDirectionRef.current = { x: 0, y: 0 };
    powerUpActiveRef.current = false;
    powerUpActiveStateRef.current = false;
    setIsInvincible(false);
    setPowerUpTime(0);
    if (powerUpTimerRef.current) {
      clearInterval(powerUpTimerRef.current);
      powerUpTimerRef.current = null;
    }
    spawnGhosts();
    createDotsAndPowerUps();
  };

  // Spawns ghosts with layered materials, glow and eye rig.
  const spawnGhosts = () => {
    const scene = sceneRef.current;
    if (!scene) return;
    ghostsRef.current.forEach(ghost => scene.remove(ghost));
    ghostsRef.current = [];

    GHOST_START_CELLS.forEach((cell, index) => {
      const color = ghostColors[index % ghostColors.length];
      const ghost = createGhostMesh(color);
      const position = gridToWorld(cell.row, cell.col);
      ghost.position.set(position.x, position.y, 0.1);

      // Store the spawn index so the ghost can be teleported back to its origin when eaten
      ghost.userData.spawnIndex = index;
      ghost.userData.direction = { x: index % 2 === 0 ? 1 : -1, y: 0 };
      ghost.userData.baseColor = color;
      ghost.userData.floatPhase = Math.random() * Math.PI * 2;
      ghost.userData.baseZ = 0.1;
      scene.add(ghost);
      ghostsRef.current.push(ghost);
    });
  };

  // Rotates pupils toward movement direction and makes them "frightened" during power-up.
  const updateGhostEyes = (ghost, moveDirection, invincible) => {
    const pupils = ghost.userData.pupils;
    const basePupilPositions = ghost.userData.basePupilPositions;
    if (!pupils || !basePupilPositions) return;

    const eyeOffset = invincible
      ? new THREE.Vector2(0, -0.05)
      : new THREE.Vector2(
        (moveDirection.x || 0) * 0.055,
        (moveDirection.y || 0) * 0.055
      );

    pupils.forEach((pupil, i) => {
      const base = basePupilPositions[i];
      pupil.position.set(base.x + eyeOffset.x, base.y + eyeOffset.y, base.z);
    });
  };

  // Spawns dots and power pellets from the maze layout and resets the dot counter.
  const createDotsAndPowerUps = () => {
    const scene = sceneRef.current;
    if (!scene) return;
    dotsRef.current.forEach(dot => scene.remove(dot));
    powerUpsRef.current.forEach(powerUp => scene.remove(powerUp));
    dotsRef.current = [];
    powerUpsRef.current = [];
    let dotCount = 0;

    mazeLayoutRef.current.forEach((row, rowIndex) => {
      [...row].forEach((cell, colIndex) => {
        if (cell === '#') return;
        if (isStartCell(rowIndex, colIndex)) return;
        if (cell === 'o') {
          const powerUp = createPowerUpMesh(rowIndex, colIndex);
          powerUpsRef.current.push(powerUp);
          scene.add(powerUp);
          return;
        }
        const dot = createDotMesh(rowIndex, colIndex);
        dotsRef.current.push(dot);
        dotCount += 1;
        scene.add(dot);
      });
    });

    setDotsLeft(dotCount);
  };

  // Updates Pac-Man's position with grid alignment and wall collision checks.
  // Also animates the mouth open/close wedge and rotates the group to face movement direction.
  const updatePacmanMovement = (deltaSeconds) => {
    const group = pacmanGroupRef.current;
    const pacman = pacmanRef.current;
    if (!group || !pacman || deltaSeconds <= 0) return;
    const currentCell = worldToGrid(group.position);
    const center = gridToWorld(currentCell.row, currentCell.col);
    const distanceToCenter = group.position.distanceTo(new THREE.Vector3(center.x, center.y, 0));
    const canTurn = distanceToCenter <= TILE_SIZE * 0.2;

    const desiredDirection = desiredDirectionRef.current;
    const activeDirection = directionRef.current;
    const isDesiredDirectionActive = desiredDirection.x === activeDirection.x && desiredDirection.y === activeDirection.y;

    if (canTurn) {
      // Only snap to the center when committing a new direction to avoid canceling movement every frame.
      if (!isDesiredDirectionActive && (desiredDirection.x !== 0 || desiredDirection.y !== 0)) {
        const desiredCell = getCellInDirection(currentCell, desiredDirection);
        if (isWalkable(desiredCell.row, desiredCell.col)) {
          group.position.set(center.x, center.y, 0.1);
          directionRef.current = { ...desiredDirection };
        }
      }
    }

    const activeDirectionNow = directionRef.current;

    // Rotate Pac-Man group to face movement direction using atan2
    if (activeDirectionNow.x !== 0 || activeDirectionNow.y !== 0) {
      const targetAngle = Math.atan2(activeDirectionNow.y, activeDirectionNow.x);
      group.rotation.z = targetAngle;
    }

    // Animate Pac-Man mouth open/close
    const mouthSpeed = 3.5;
    pacmanMouthAngleRef.current += pacmanMouthDirRef.current * mouthSpeed * deltaSeconds;
    if (pacmanMouthAngleRef.current > 0.38) {
      pacmanMouthAngleRef.current = 0.38;
      pacmanMouthDirRef.current = -1;
    } else if (pacmanMouthAngleRef.current < 0.02) {
      pacmanMouthAngleRef.current = 0.02;
      pacmanMouthDirRef.current = 1;
    }

    // Rebuild Pac-Man geometry with updated mouth angle
    const newGeo = createPacmanShape(pacmanMouthAngleRef.current);
    pacman.geometry.dispose();
    pacman.geometry = newGeo;

    if (activeDirectionNow.x === 0 && activeDirectionNow.y === 0) return;

    const nextCell = getCellInDirection(currentCell, activeDirectionNow);
    const nextCellWalkable = isWalkable(nextCell.row, nextCell.col);
    const step = speedRef.current * deltaSeconds;

    if (!nextCellWalkable && canTurn) {
      directionRef.current = { x: 0, y: 0 };
      group.position.set(center.x, center.y, 0.1);
      return;
    }

    group.position.x += activeDirectionNow.x * step;
    group.position.y += activeDirectionNow.y * step;
    applyHorizontalTunnelWrap(group);
  };

  // Checks for Pac-Man overlap with dots and removes eaten ones, awarding score.
  const handleDotCollisions = () => {
    const scene = sceneRef.current;
    const group = pacmanGroupRef.current;
    if (!scene || !group) return;
    for (let i = dotsRef.current.length - 1; i >= 0; i -= 1) {
      const dot = dotsRef.current[i];
      const dx = dot.position.x - group.position.x;
      const dy = dot.position.y - group.position.y;
      if (Math.sqrt(dx * dx + dy * dy) < TILE_SIZE * 0.4) {
        scene.remove(dot);
        dotsRef.current.splice(i, 1);
        setScore(prev => prev + 10);
        setDotsLeft(prev => prev - 1);
        playSfx('dot');
      }
    }
  };

  // Checks for Pac-Man overlap with power pellets and triggers invincibility mode.
  const handlePowerUpCollisions = () => {
    const scene = sceneRef.current;
    const group = pacmanGroupRef.current;
    if (!scene || !group) return;
    for (let i = powerUpsRef.current.length - 1; i >= 0; i -= 1) {
      const powerUp = powerUpsRef.current[i];
      const dx = powerUp.position.x - group.position.x;
      const dy = powerUp.position.y - group.position.y;
      if (Math.sqrt(dx * dx + dy * dy) < TILE_SIZE * 0.6) {
        scene.remove(powerUp);
        powerUpsRef.current.splice(i, 1);
        activatePowerUp();
        playSfx('powerup');
      }
    }
  };

  // Handles player damage: consume one life, respawn, and trigger game over at zero lives.
  const handlePlayerHit = () => {
    if (gameOverRef.current) return;

    const nextLives = Math.max(livesRef.current - 1, 0);
    livesRef.current = nextLives;
    setLives(nextLives);

    if (powerUpTimerRef.current) {
      clearInterval(powerUpTimerRef.current);
      powerUpTimerRef.current = null;
    }
    powerUpActiveRef.current = false;
    powerUpActiveStateRef.current = false;
    setIsInvincible(false);
    setPowerUpTime(0);
    directionRef.current = { x: 0, y: 0 };
    desiredDirectionRef.current = { x: 0, y: 0 };
    if (pacmanGlowRef.current) pacmanGlowRef.current.material.opacity = 0;

    if (nextLives <= 0) {
      setGameStarted(false);
      setGameOver(true);
      playSfx('gameover');
      return;
    }

    setGameStarted(false);
    const group = pacmanGroupRef.current;
    if (group) {
      const startPosition = gridToWorld(PACMAN_START_CELL.row, PACMAN_START_CELL.col);
      group.position.set(startPosition.x, startPosition.y, 0.1);
      group.rotation.z = 0;
    }
    spawnGhosts();

    if (lifeRespawnTimeoutRef.current) {
      clearTimeout(lifeRespawnTimeoutRef.current);
    }
    lifeRespawnTimeoutRef.current = setTimeout(() => {
      if (!gameOverRef.current && !showMenuRef.current) {
        setGameStarted(true);
      }
    }, 700);
  };

  // Moves ghosts toward or away from Pac-Man depending on invincibility state.
  // Ghost color turns blue-white while fleeing, reverts to original color otherwise.
  const updateGhostMovement = (deltaSeconds, invincible) => {
    const group = pacmanGroupRef.current;
    if (!group || deltaSeconds <= 0) return;
    for (let i = 0; i < ghostsRef.current.length; i += 1) {
      const ghost = ghostsRef.current[i];
      const currentCell = worldToGrid(ghost.position);
      const center = gridToWorld(currentCell.row, currentCell.col);
      const distanceToCenter = Math.hypot(ghost.position.x - center.x, ghost.position.y - center.y);
      const canTurn = distanceToCenter <= TILE_SIZE * 0.25;
      const currentDirection = ghost.userData.direction || { x: 1, y: 0 };

      if (canTurn) {
        const possibleDirections = getValidDirections(currentCell.row, currentCell.col);
        const filteredDirections = possibleDirections.filter(dir => !(dir.x === -currentDirection.x && dir.y === -currentDirection.y));
        const candidateDirections = filteredDirections.length ? filteredDirections : possibleDirections;
        if (candidateDirections.length) {
          const targetPosition = group.position;
          let bestDirection = candidateDirections[0];
          let bestScore = invincible ? -Infinity : Infinity;
          candidateDirections.forEach((direction) => {
            const nextCell = getCellInDirection(currentCell, direction);
            const nextWorld = gridToWorld(nextCell.row, nextCell.col);
            const distance = Math.hypot(nextWorld.x - targetPosition.x, nextWorld.y - targetPosition.y);
            if (invincible ? distance > bestScore : distance < bestScore) {
              bestScore = distance;
              bestDirection = direction;
            }
          });
          ghost.userData.direction = bestDirection;
        }
      }

      const moveDirection = ghost.userData.direction || { x: 0, y: 0 };
      ghost.position.x += moveDirection.x * ghostSpeedRef.current * deltaSeconds;
      ghost.position.y += moveDirection.y * ghostSpeedRef.current * deltaSeconds;
      applyHorizontalTunnelWrap(ghost);

      // Secondary animation: float and slight skirt breathing.
      ghost.userData.floatPhase += deltaSeconds * 4.8;
      ghost.position.z = (ghost.userData.baseZ || 0.1) + Math.sin(ghost.userData.floatPhase) * 0.045;
      if (ghost.userData.body) {
        const skirtWave = 1 + Math.sin(ghost.userData.floatPhase * 1.7 + i) * 0.025;
        ghost.userData.body.scale.y = skirtWave;
      }

      // Keep each ghost's identity color with realistic frightened tint.
      const bodyMaterial = ghost.userData.bodyMaterial;
      const glowMaterial = ghost.userData.glowMaterial;
      const haloMaterial = ghost.userData.haloMaterial;
      const glowMesh = ghost.userData.glowMesh;
      const haloMesh = ghost.userData.haloMesh;
      if (ghost.userData.baseColor && bodyMaterial) {
        const baseColor = new THREE.Color(ghost.userData.baseColor);
        bodyMaterial.color.copy(baseColor);
        bodyMaterial.emissive.copy(baseColor).multiplyScalar(0.28);

        if (invincible) {
          bodyMaterial.color.lerp(new THREE.Color(0x94b8ff), 0.58);
          bodyMaterial.emissive.lerp(new THREE.Color(0x3164ff), 0.7);
        }

        if (glowMaterial) {
          glowMaterial.color.copy(bodyMaterial.color);
          glowMaterial.opacity = invincible ? 0.42 : 0.3;
        }

        if (haloMaterial) {
          haloMaterial.color.copy(bodyMaterial.color);
          haloMaterial.opacity = invincible ? 0.24 : 0.16;
        }

        const glowPulse = 1 + Math.sin(ghost.userData.floatPhase * 2.2 + i) * 0.06;
        const haloPulse = 1 + Math.sin(ghost.userData.floatPhase * 1.6 + i * 0.5) * 0.08;
        if (glowMesh) {
          glowMesh.scale.set(1.16 * glowPulse, 1.16 * glowPulse, 1);
        }
        if (haloMesh) {
          haloMesh.scale.set(1.34 * haloPulse, 1.34 * haloPulse, 1);
        }
      }

      updateGhostEyes(ghost, moveDirection, invincible);

      const dx = ghost.position.x - group.position.x;
      const dy = ghost.position.y - group.position.y;
      if (Math.sqrt(dx * dx + dy * dy) < TILE_SIZE * 0.6) {
        if (invincible) {
          createGhostParticles(ghost.position);
          setScore(prev => prev + 100);
          playSfx('ghost');
          // Respawn the ghost at its original starting cell instead of removing it
          const spawnIndex = ghost.userData.spawnIndex;
          const spawnCell = GHOST_START_CELLS[spawnIndex];
          const spawnPos = gridToWorld(spawnCell.row, spawnCell.col);
          ghost.position.set(spawnPos.x, spawnPos.y, 0.1);
          ghost.userData.direction = { x: spawnIndex % 2 === 0 ? 1 : -1, y: 0 };
          ghost.userData.floatPhase = Math.random() * Math.PI * 2;
          if (ghost.userData.bodyMaterial) {
            ghost.userData.bodyMaterial.color.setHex(ghost.userData.baseColor);
            ghost.userData.bodyMaterial.emissive
              .setHex(ghost.userData.baseColor)
              .multiplyScalar(0.28);
          }
        } else {
          handlePlayerHit();
          return;
        }
      }
    }
  };

  // Advances to the next level when all dots are collected, increasing speed.
  const handleLevelComplete = () => {
    if (dotsRef.current.length !== 0) return;
    setGameStarted(false);
    setLevel(prev => prev + 1);
    speedRef.current += 0.4;
    ghostSpeedRef.current += 0.3;
    setTimeout(() => {
      resetLevelState(true);
      setGameStarted(true);
    }, 800);
  };

  // Constructs the neon-blue maze walls using BoxGeometry with emissive glow material.
  // Each '#' in the layout becomes a thick, glowing blue wall tile.
  const createMaze = () => {
    const scene = sceneRef.current;
    if (!scene) return;
    wallsRef.current.forEach(wall => scene.remove(wall));
    wallsRef.current = [];

    // Outer wall material: vivid neon blue with emissive glow
    const wallMaterial = new THREE.MeshBasicMaterial({
      color: 0x2121de,
    });

    mazeLayoutRef.current.forEach((row, rowIndex) => {
      [...row].forEach((cell, colIndex) => {
        if (cell !== '#') return;
        const wallGeometry = new THREE.BoxGeometry(TILE_SIZE * 0.96, TILE_SIZE * 0.96, 0.4);
        const wall = new THREE.Mesh(wallGeometry, wallMaterial);
        const position = gridToWorld(rowIndex, colIndex);
        wall.position.set(position.x, position.y, 0);
        wallsRef.current.push(wall);
        scene.add(wall);

        // Bright highlight edge overlay for a neon border glow effect
        const edgeGeo = new THREE.EdgesGeometry(wallGeometry);
        const edgeMat = new THREE.LineBasicMaterial({ color: 0x4444ff, linewidth: 2 });
        const edges = new THREE.LineSegments(edgeGeo, edgeMat);
        edges.position.copy(wall.position);
        scene.add(edges);
        wallsRef.current.push(edges);
      });
    });
  };

  // Main animation loop: updates game state and renders the Three.js scene every frame.
  const animate = (timestamp = 0) => {
    animationFrameRef.current = requestAnimationFrame(animate);
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const group = pacmanGroupRef.current;
    if (!scene || !camera || !renderer || !group) return;

    const deltaSeconds = lastFrameTimeRef.current
      ? Math.min((timestamp - lastFrameTimeRef.current) / 1000, 0.05)
      : 0;
    lastFrameTimeRef.current = timestamp;

    // Animate power pellet glow rings pulsing over time
    const t = timestamp / 600;
    powerUpsRef.current.forEach(pu => {
      if (pu.userData.glowRing) {
        pu.userData.glowRing.material.opacity = 0.3 + 0.4 * Math.abs(Math.sin(t));
        pu.scale.setScalar(0.85 + 0.18 * Math.abs(Math.sin(t)));
      }
    });

    // Animate subtle point light flicker for retro screen atmosphere
    if (pointLightRef.current) {
      pointLightRef.current.intensity = 1.1 + 0.15 * Math.sin(timestamp / 300);
    }

    const invincibleNow = powerUpActiveStateRef.current;

    if (gameStartedRef.current && !gameOverRef.current && !showMenuRef.current) {
      updatePacmanMovement(deltaSeconds);
      handleDotCollisions();
      handlePowerUpCollisions();
      updateGhostMovement(deltaSeconds, invincibleNow);
      handleLevelComplete();
    }

    renderer.render(scene, camera);
  };

  // Creates colorful particle burst at a position when a ghost is eaten.
  const createGhostParticles = (position) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const particleCount = 20;
    for (let i = 0; i < particleCount; i++) {
      const particleGeometry = new THREE.CircleGeometry(0.18, 6);
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(Math.random() * 0xffffff),
        transparent: true,
        opacity: 1,
      });
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      particle.position.copy(position);
      scene.add(particle);

      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        0
      );

      // Fades the particle out and removes it from the scene when invisible
      const animateParticle = () => {
        particle.position.add(velocity);
        velocity.multiplyScalar(0.92);
        particleMaterial.opacity -= 0.025;
        if (particleMaterial.opacity <= 0) {
          scene.remove(particle);
          particleGeometry.dispose();
          particleMaterial.dispose();
        } else {
          requestAnimationFrame(animateParticle);
        }
      };
      animateParticle();
    }
  };

  // Activates invincibility power-up mode with glow and countdown timer.
  const activatePowerUp = () => {
    if (powerUpActiveRef.current) clearTimeout(powerUpTimerRef.current);
    setIsInvincible(true);
    powerUpActiveStateRef.current = true;
    setPowerUpTime(10);
    powerUpActiveRef.current = true;
    if (pacmanGlowRef.current) pacmanGlowRef.current.material.opacity = 0.55;

    powerUpTimerRef.current = setInterval(() => {
      setPowerUpTime(prev => {
        if (prev <= 1) {
          setIsInvincible(false);
          powerUpActiveRef.current = false;
          powerUpActiveStateRef.current = false;
          if (pacmanGlowRef.current) pacmanGlowRef.current.material.opacity = 0;
          clearInterval(powerUpTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Keeps keyboard focus on the game container so arrow keys are captured reliably.
  const ensureInputFocus = () => {
    const container = containerRef.current;
    if (!container) return;
    if (document.activeElement !== container) {
      container.focus();
    }
  };

  // Handles keyboard arrow keys to update Pac-Man's desired movement direction.
  // Uses refs instead of state variables to avoid stale closure bugs in the event listener.
  // Applies directional input consistently across keyboard, swipe/tap and on-screen d-pad.
  const applyDirectionInput = (nextDirection) => {
    if (!gameStartedRef.current && !showMenuRef.current && !gameOverRef.current) {
      setGameStarted(true);
    }
    if (gameOverRef.current || showMenuRef.current) return;
    desiredDirectionRef.current = { ...nextDirection };
  };

  // Handles keyboard arrow keys to update Pac-Man's desired movement direction.
  // Uses refs instead of state variables to avoid stale closure bugs in the event listener.
  const handleKeyDown = (e) => {
    if (showHowToPlayRef.current || showSettingsRef.current || showCreditsRef.current) return;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        applyDirectionInput({ x: 0, y: 1 });
        break;
      case 'ArrowDown':
        e.preventDefault();
        applyDirectionInput({ x: 0, y: -1 });
        break;
      case 'ArrowLeft':
        e.preventDefault();
        applyDirectionInput({ x: -1, y: 0 });
        break;
      case 'ArrowRight':
        e.preventDefault();
        applyDirectionInput({ x: 1, y: 0 });
        break;
      default: break;
    }
  };

  // Captures the initial touch coordinates for swipe gesture detection.
  const handleTouchStart = (e) => {
    if (isTouchOnInteractiveElement(e.target) || showMenuRef.current || showHowToPlayRef.current || showSettingsRef.current || showCreditsRef.current) {
      return;
    }
    e.preventDefault();
    ensureInputFocus();
    if (e.touches.length === 0) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    touchEndRef.current = { ...touchStartRef.current };
  };

  // Updates the current touch end position during a swipe gesture.
  const handleTouchMove = (e) => {
    if (isTouchOnInteractiveElement(e.target) || showMenuRef.current || showHowToPlayRef.current || showSettingsRef.current || showCreditsRef.current) {
      return;
    }
    e.preventDefault();
    if (e.touches.length === 0) return;
    touchEndRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  // Converts a tap position into one of the 4 cardinal directions relative to Pac-Man.
  const setDirectionFromTap = (clientX, clientY) => {
    const group = pacmanGroupRef.current;
    const camera = cameraRef.current;
    const container = containerRef.current;
    if (!group || !camera || !container) return;

    const bounds = container.getBoundingClientRect();
    const pacmanScreen = group.position.clone().project(camera);
    const pacmanX = bounds.left + ((pacmanScreen.x + 1) / 2) * bounds.width;
    const pacmanY = bounds.top + ((1 - pacmanScreen.y) / 2) * bounds.height;

    const diffX = clientX - pacmanX;
    const diffY = clientY - pacmanY;

    if (Math.abs(diffX) > Math.abs(diffY)) {
      applyDirectionInput(diffX > 0 ? { x: 1, y: 0 } : { x: -1, y: 0 });
    } else {
      applyDirectionInput(diffY > 0 ? { x: 0, y: -1 } : { x: 0, y: 1 });
    }
  };

  // Converts the completed swipe gesture into a movement direction for Pac-Man.
  // Uses refs instead of state variables to avoid stale closure bugs in the event listener.
  const handleTouchEnd = (e) => {
    if (isTouchOnInteractiveElement(e?.target) || showMenuRef.current || showHowToPlayRef.current || showSettingsRef.current || showCreditsRef.current) {
      return;
    }
    if (e) e.preventDefault();
    if (gameOverRef.current || showMenuRef.current) return;

    if (e?.changedTouches?.length) {
      touchEndRef.current = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    }

    const diffX = touchStartRef.current.x - touchEndRef.current.x;
    const diffY = touchStartRef.current.y - touchEndRef.current.y;
    const minSwipeDistance = 24;
    if (Math.abs(diffX) < minSwipeDistance && Math.abs(diffY) < minSwipeDistance) {
      setDirectionFromTap(touchEndRef.current.x, touchEndRef.current.y);
      return;
    }

    if (Math.abs(diffX) > Math.abs(diffY)) {
      applyDirectionInput(diffX > 0 ? { x: -1, y: 0 } : { x: 1, y: 0 });
    } else {
      applyDirectionInput(diffY > 0 ? { x: 0, y: 1 } : { x: 0, y: -1 });
    }
  };

  // Renders one on-screen d-pad button and safely applies direction on mobile pointer press.
  const renderDpadButton = (direction, label, ariaLabel) => {
    // Handles pointer presses while preventing accidental browser gestures and focus loss.
    const handleDpadPointerDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      ensureInputFocus();
      applyDirectionInput(direction);
    };

    return (
      <button
        aria-label={ariaLabel}
        onPointerDown={handleDpadPointerDown}
        style={{
          width: 56,
          height: 56,
          borderRadius: 10,
          background: 'rgba(255, 208, 0, 0.12)',
          border: '1.5px solid rgba(255, 208, 0, 0.45)',
          color: '#ffd24a',
          fontSize: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          boxShadow: '0 0 10px rgba(255, 208, 0, 0.32)',
          transition: 'background 0.1s, box-shadow 0.1s',
        }}
        onPointerEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 208, 0, 0.26)';
          e.currentTarget.style.boxShadow = '0 0 18px rgba(255, 208, 0, 0.55)';
        }}
        onPointerLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 208, 0, 0.12)';
          e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 208, 0, 0.32)';
        }}
      >
        {label}
      </button>
    );
  };

  // Resets all game state back to menu defaults for a fresh run.
  const resetGame = () => {
    setScore(0);
    setLevel(1);
    setLives(INITIAL_LIVES);
    livesRef.current = INITIAL_LIVES;
    setGameOver(false);
    setIsInvincible(false);
    powerUpActiveStateRef.current = false;
    setPowerUpTime(0);
    directionRef.current = { x: 0, y: 0 };
    desiredDirectionRef.current = { x: 0, y: 0 };
    speedRef.current = 5.2;
    ghostSpeedRef.current = 3;
    const group = pacmanGroupRef.current;
    if (group) {
      const startPosition = gridToWorld(PACMAN_START_CELL.row, PACMAN_START_CELL.col);
      group.position.set(startPosition.x, startPosition.y, 0.1);
      group.rotation.z = 0;
    }
    if (pacmanGlowRef.current) pacmanGlowRef.current.material.opacity = 0;
    resetLevelState();
    backgroundMusicRef.current.stop();
    setShowMenu(true);
  };

  // Starts a new game run from the main menu, resuming audio and resizing canvas.
  const startGame = () => {
    setShowMenu(false);
    setGameOver(false);
    setGameStarted(true);
    resetLevelState(true);
    queueRendererResize();
    ensureInputFocus();
    ensureAudioContext();
    if (isMusicEnabled && !backgroundMusicRef.current.playing()) {
      backgroundMusicRef.current.play();
    }
  };

  // Toggles soundtrack playback during gameplay.
  const toggleSoundtrack = () => {
    const nextEnabled = !isMusicEnabled;
    setIsMusicEnabled(nextEnabled);

    if (nextEnabled) {
      ensureAudioContext();
      if (!backgroundMusicRef.current.playing()) {
        backgroundMusicRef.current.play();
      }
    } else if (backgroundMusicRef.current.playing()) {
      backgroundMusicRef.current.pause();
    }
  };

  // Handles window resize events to keep the renderer and camera proportional.
  const handleResize = () => {
    updateRendererSize();
  };

  // Initializes the scene, event listeners, ResizeObserver, and returns cleanup on unmount.
  useEffect(() => {
    initScene();
    window.addEventListener('resize', handleResize);
    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd, { passive: false });
      ensureInputFocus();
    }

    // Observe the canvas mount element for size changes caused by layout shifts
    // (e.g. sidebar opening/closing) and keep the renderer in sync.
    let resizeObserver = null;
    if (mountRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateRendererSize();
      });
      resizeObserver.observe(mountRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
      // Disconnect the ResizeObserver to avoid memory leaks on unmount
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (powerUpTimerRef.current) clearInterval(powerUpTimerRef.current);
      if (lifeRespawnTimeoutRef.current) clearTimeout(lifeRespawnTimeoutRef.current);
      backgroundMusicRef.current.stop();
      if (rendererRef.current) rendererRef.current.dispose();
      if (mountRef.current && rendererRef.current?.domElement) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
    };
  }, []);

  // Keeps gameStartedRef in sync with the gameStarted state for use in stale closures.
  useEffect(() => { gameStartedRef.current = gameStarted; }, [gameStarted]);

  // Keeps gameOverRef in sync with the gameOver state for use in stale closures.
  useEffect(() => { gameOverRef.current = gameOver; }, [gameOver]);

  // Keeps livesRef in sync with the lives state for use in stale closures.
  useEffect(() => { livesRef.current = lives; }, [lives]);

  // Keeps showMenuRef in sync with the showMenu state for use in stale closures.
  useEffect(() => { showMenuRef.current = showMenu; }, [showMenu]);
  useEffect(() => { showHowToPlayRef.current = showHowToPlay; }, [showHowToPlay]);
  useEffect(() => { showSettingsRef.current = showSettings; }, [showSettings]);
  useEffect(() => { showCreditsRef.current = showCredits; }, [showCredits]);

  // Re-focuses the container whenever overlay visibility changes to restore input.
  useEffect(() => {
    ensureInputFocus();
  }, [showMenu, showHowToPlay, showSettings, showCredits]);

  return (
    <div
      className="ball-ball-container"
      ref={containerRef}
      tabIndex={0}
      onPointerDown={ensureInputFocus}
      onKeyDown={handleKeyDown}
    >
      {/* CRT scanline overlay applied via CSS pseudo-element for retro atmosphere */}
      <div className="crt-overlay" aria-hidden="true" />

      {showMenu && (
        <div className="main-menu">
          <h1 className="arcade-title">BALL BALL</h1>
          <div className="menu-buttons">
            <button onClick={startGame}>Start Game</button>
            <button onClick={() => setShowHowToPlay(true)}>How to Play</button>
            <button onClick={() => setShowSettings(true)}>Settings</button>
            {/* <button onClick={() => setShowCredits(true)}>Credits</button> */}
          </div>
        </div>
      )}
      {showHowToPlay && (
        <div className="how-to-play-menu">
          <h2>How to Play</h2>
          <p>Use arrow keys or swipe to move Player.</p>
          <p>Eat all the dots to advance to the next level.</p>
          <p>Eat power-ups to become invincible and chase ghosts!</p>
          <button onClick={() => setShowHowToPlay(false)}>Back</button>
        </div>
      )}
      {showSettings && (
        <div className="settings-menu">
          <h2>Settings</h2>
          <div className="volume-control">
            <label>Music Volume: {Math.round(musicVolume * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={musicVolume}
              onChange={(e) => {
                setMusicVolume(parseFloat(e.target.value));
                backgroundMusicRef.current.volume(parseFloat(e.target.value));
              }}
            />
          </div>
          <div className="volume-control">
            <label>SFX Volume: {Math.round(sfxVolume * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={sfxVolume}
              onChange={(e) => {
                setSfxVolume(parseFloat(e.target.value));
              }}
            />
          </div>
          <button onClick={() => setShowSettings(false)}>Back</button>
        </div>
      )}
      {showCredits && (
        <div className="credits-menu">
          <h2>Credits</h2>
          <p>Game by Andrea Giuggioli</p>
          <button onClick={() => setShowCredits(false)}>Back</button>
        </div>
      )}
      <div ref={mountRef} className="ball-ball-canvas" />
      {!showMenu && !showHowToPlay && !showSettings && !showCredits && (
        <>
          <button className="soundtrack-toggle-button" onClick={toggleSoundtrack}>
            {isMusicEnabled ? 'Soundtrack: ON' : 'Soundtrack: OFF'}
          </button>
          <div className="score-display">Score: {score}</div>
          <div className="level-display">Level: {level}</div>
          <div className="lives-display">Lives: {lives}</div>
          <div
            style={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              zIndex: 16,
              display: 'grid',
              gridTemplateColumns: '56px 56px 56px',
              gridTemplateRows: '56px 56px 56px',
              gap: 6,
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            <span />
            {renderDpadButton({ x: 0, y: 1 }, '▲', 'Move up')}
            <span />
            {renderDpadButton({ x: -1, y: 0 }, '◀', 'Move left')}
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 10,
                border: '1px solid rgba(255, 208, 0, 0.15)',
                background: 'rgba(255, 208, 0, 0.03)',
              }}
            />
            {renderDpadButton({ x: 1, y: 0 }, '▶', 'Move right')}
            <span />
            {renderDpadButton({ x: 0, y: -1 }, '▼', 'Move down')}
            <span />
          </div>
          {isInvincible && <div className="powerup-timer">Power: {powerUpTime}s</div>}
          {gameOver && (
            <div className="game-over-overlay">
              <div className="game-over-content">
                <h1>GAME OVER</h1>
                <p>Score: {score}</p>
                <button onClick={resetGame}>Back to Menu</button>
              </div>
            </div>
          )}
          {!gameStarted && !gameOver && (
            <div className="start-overlay">
              <h1>Level {level}</h1>
              <p>Ready?</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BallBall;
